import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useUpdateUser, useGetUser } from "@src/presentation/hooks/users"
import { useRoles } from "@src/presentation/hooks/rbac"
import { ToastService } from "@src/shared/services/toast.service"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Input,
    Select,
    SelectItem,
    Switch,
    Divider,
    Spinner,
    Chip,
    Avatar
} from "@heroui/react"
import {
    ChevronLeft,
    Edit,
    Mail,
    Shield,
    User,
    AlertCircle,
    CheckCircle,
    ShieldCheck,
    UserCheck,
    XCircle
} from "lucide-react"

export default function EditUserPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        middle_name: "",
        email: "",
        role_ids: [] as string[],
        is_active: true,
        is_verified: false,
        is_banned: false,
    })

    const [errors, setErrors] = useState<Record<string, string>>({})

    // Fetch user data
    const { data: user, isLoading: isLoadingUser, error: fetchError } = useGetUser(id)

    // Fetch roles
    const { data: rolesData, isLoading: isLoadingRoles } = useRoles()
    const roles = rolesData || []

    // Update user mutation
    const { mutate: updateUser, isPending } = useUpdateUser({
        onSuccess: () => {
            ToastService.success("User updated successfully")
            navigate(`/users/${id}`)
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to update user")
        }
    })

    // Populate form when user data is loaded
    useEffect(() => {
        if (user) {
            setFormData({
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                middle_name: user.middle_name || "",
                email: user.email || "",
                role_ids: user.roles?.map(role => role.role_id) || [],
                is_active: user.is_active,
                is_verified: user.is_verified,
                is_banned: user.is_banned,
            })
        }
    }, [user])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()

        // Validation
        const newErrors: Record<string, string> = {}

        if (!formData.first_name) newErrors.first_name = "First name is required"
        if (!formData.last_name) newErrors.last_name = "Last name is required"
        if (!formData.email) newErrors.email = "Email is required"
        if (formData.role_ids.length === 0) newErrors.role_ids = "At least one role is required"

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        // Update user
        if (id) {
            updateUser({
                userId: id,
                data: {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    middle_name: formData.middle_name || null,
                    email: formData.email,
                    role_ids: formData.role_ids,
                    is_active: formData.is_active,
                    is_verified: formData.is_verified,
                    is_banned: formData.is_banned,
                }
            })
        }
    }

    // Loading state
    if (isLoadingUser || isLoadingRoles) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <Spinner size="lg" color="primary" />
                    <p className="mt-4 text-sm text-default-500 font-medium">Loading user data...</p>
                </div>
            </div>
        )
    }

    // Error state
    if (fetchError || !user) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50 flex items-center justify-center p-4">
                <Card className="max-w-md border border-border rounded">
                    <CardBody className="text-center p-8">
                        <div className="p-4 rounded bg-danger/10 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                            <AlertCircle className="h-10 w-10 text-danger" />
                        </div>
                        <h2 className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-2">User Not Found</h2>
                        <p className="text-default-500 mb-6">
                            {fetchError?.message || "The user you're trying to edit doesn't exist or has been removed."}
                        </p>
                        <Button
                            color="primary"
                            onPress={() => navigate("/users")}
                        >
                            Back to All Users
                        </Button>
                    </CardBody>
                </Card>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                                startContent={<ChevronLeft className="h-4 w-4" />}
                                className="hover:scale-105 transition-transform flex-shrink-0"
                            >
                                <span className="hidden sm:inline">Back</span>
                                <span className="sm:hidden">Back</span>
                            </Button>
                            <Divider orientation="vertical" className="h-6 hidden sm:block" />
                            <div className="flex items-center gap-3">
                                <Avatar
                                    name={user.display_name}
                                    src={user.profile_picture_url || undefined}
                                    className="h-8 w-8"
                                />
                                <div>
                                    <h1 className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        Edit User
                                    </h1>
                                    <p className="text-xs text-default-500 font-medium">
                                        Update {user.display_name}'s information
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="max-w-3xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Personal Information */}
                        <Card className="border border-border bg-secondary rounded shadow-none">
                            <CardHeader className="border-b border-divider pb-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded bg-primary/10">
                                        <User className="h-4 w-4 text-primary" />
                                    </div>
                                    <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        Personal Information
                                    </h2>
                                </div>
                            </CardHeader>
                            <CardBody className="p-6">
                                <div className="grid gap-4">
                                    <Input
                                        label="First Name"
                                        placeholder="Enter first name"
                                        value={formData.first_name}
                                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                        isInvalid={!!errors.first_name}
                                        errorMessage={errors.first_name}
                                        startContent={<User className="h-4 w-4 text-default-400" />}
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                        isRequired
                                    />
                                    <Input
                                        label="Middle Name (Optional)"
                                        placeholder="Enter middle name"
                                        value={formData.middle_name}
                                        onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                                        startContent={<User className="h-4 w-4 text-default-400" />}
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                    />
                                    <Input
                                        label="Last Name"
                                        placeholder="Enter last name"
                                        value={formData.last_name}
                                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                        isInvalid={!!errors.last_name}
                                        errorMessage={errors.last_name}
                                        startContent={<User className="h-4 w-4 text-default-400" />}
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                        isRequired
                                    />
                                    <Input
                                        type="email"
                                        label="Email Address"
                                        placeholder="admin@example.com"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        isInvalid={!!errors.email}
                                        errorMessage={errors.email}
                                        startContent={<Mail className="h-4 w-4 text-default-400" />}
                                        classNames={{
                                            input: "font-medium",
                                            inputWrapper: "rounded border border-border"
                                        }}
                                        isRequired
                                    />

                                    <Select
                                        label="Roles"
                                        placeholder="Select roles"
                                        selectionMode="multiple"
                                        selectedKeys={new Set(formData.role_ids)}
                                        onSelectionChange={(keys) => {
                                            const selectedRoles = Array.from(keys as Set<string>)
                                            setFormData({ ...formData, role_ids: selectedRoles })
                                        }}
                                        isInvalid={!!errors.role_ids}
                                        errorMessage={errors.role_ids}
                                        isDisabled
                                        startContent={<Shield className="h-4 w-4 text-default-400" />}
                                        classNames={{
                                            trigger: "rounded border border-border",
                                            value: "font-medium"
                                        }}
                                        renderValue={(items) => {
                                            return (
                                                <div className="flex flex-wrap gap-1">
                                                    {items.map((item) => (
                                                        <Chip key={item.key} size="sm" variant="flat" color="primary">
                                                            {item.textValue}
                                                        </Chip>
                                                    ))}
                                                </div>
                                            )
                                        }}
                                        isRequired
                                    >
                                        {roles.map((role) => (
                                            <SelectItem key={role.roleId} textValue={role.displayName}>
                                                {role.displayName}
                                            </SelectItem>
                                        ))}
                                    </Select>

                                    {/* Display phone number (read-only) */}
                                    {user.phone_number && (
                                        <div className="flex items-start gap-3 p-3 rounded border border-border bg-default-50/50">
                                            <Mail className="h-4 w-4 text-default-500 mt-2" />
                                            <div className="flex-1">
                                                <p className="text-xs text-default-500 font-medium mb-1">Phone Number</p>
                                                <p className="text-sm text-foreground">{user.phone_number}</p>
                                                <p className="text-xs text-default-400 mt-1">Phone number cannot be changed</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>

                        {/* Account Status */}
                        <Card className="border border-border bg-secondary rounded shadow-none">
                            <CardHeader className="border-b border-divider pb-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded bg-success/10">
                                        <UserCheck className="h-4 w-4 text-success" />
                                    </div>
                                    <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                        Account Status
                                    </h2>
                                </div>
                            </CardHeader>
                            <CardBody className="p-6">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 rounded border border-border bg-default-50/50">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded ${formData.is_active ? 'bg-success/10' : 'bg-warning/10'}`}>
                                                {formData.is_active ? (
                                                    <CheckCircle className="h-4 w-4 text-success" />
                                                ) : (
                                                    <XCircle className="h-4 w-4 text-warning" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-foreground">Active Status</p>
                                                <p className="text-xs text-default-500">
                                                    {formData.is_active ? "User can access the system" : "User cannot access the system"}
                                                </p>
                                            </div>
                                        </div>
                                        <Switch
                                            isSelected={formData.is_active}
                                            onValueChange={(value) => setFormData({ ...formData, is_active: value })}
                                            color="success"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 rounded border border-border bg-default-50/50">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded ${formData.is_verified ? 'bg-success/10' : 'bg-warning/10'}`}>
                                                {formData.is_verified ? (
                                                    <ShieldCheck className="h-4 w-4 text-success" />
                                                ) : (
                                                    <AlertCircle className="h-4 w-4 text-warning" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-foreground">Email Verification</p>
                                                <p className="text-xs text-default-500">
                                                    {formData.is_verified ? "Email has been verified" : "Email needs verification"}
                                                </p>
                                            </div>
                                        </div>
                                        <Switch
                                            isSelected={formData.is_verified}
                                            onValueChange={(value) => setFormData({ ...formData, is_verified: value })}
                                            color="success"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 rounded border border-border bg-default-50/50">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded ${formData.is_banned ? 'bg-danger/10' : 'bg-success/10'}`}>
                                                {formData.is_banned ? (
                                                    <XCircle className="h-4 w-4 text-danger" />
                                                ) : (
                                                    <CheckCircle className="h-4 w-4 text-success" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-foreground">Ban Status</p>
                                                <p className="text-xs text-default-500">
                                                    {formData.is_banned ? "User is banned from the system" : "User is not banned"}
                                                </p>
                                            </div>
                                        </div>
                                        <Switch
                                            isSelected={formData.is_banned}
                                            onValueChange={(value) => setFormData({ ...formData, is_banned: value })}
                                            color="danger"
                                        />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Current Roles (Read-only) */}
                        {user.roles && user.roles.length > 0 && (
                            <Card className="border border-border bg-secondary rounded shadow-none">
                                <CardHeader className="border-b border-divider pb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="p-2 rounded bg-primary/10">
                                            <ShieldCheck className="h-4 w-4 text-primary" />
                                        </div>
                                        <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                                            Current Roles
                                        </h2>
                                    </div>
                                </CardHeader>
                                <CardBody className="p-6">
                                    <div className="flex flex-wrap gap-2">
                                        {user.roles.map((role) => (
                                            <Chip key={role.role_id} color="primary" variant="flat">
                                                {role.display_name}
                                            </Chip>
                                        ))}
                                    </div>
                                    <p className="text-xs text-default-400 mt-3">
                                        Role management is handled separately. Contact a system administrator to change user roles.
                                    </p>
                                </CardBody>
                            </Card>
                        )}

                        {/* Submit Button */}
                        <div className="flex items-center justify-end gap-3 pt-4">
                            <Button
                                variant="flat"
                                onPress={() => navigate(-1)}
                                className="rounded"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                isLoading={isPending}
                                startContent={!isPending ? <Edit className="h-4 w-4" /> : null}
                                className="rounded"
                            >
                                {isPending ? "Updating..." : "Update User"}
                            </Button>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    )
}
